import { TimeOverride } from "./timeOverrideUtils.js";
import { dayMs, hourMs, minuteMs, secondMs } from "../timeConstants.js";

export const DayLookup = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const LongMonthLookup = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
export const ShortMonthLookup = ["Jan", "Feb", "Mar", "Apr", "May", "June", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const ShortDayLookup = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

/**
 * 2:30 pm on Feb 15 in different format strings:
 * hh:mm nn -> 02:30 pm
 * HH:mm NN -> 14:30 P2 (legacy gardenspade bug: `NN` becomes `PM`, then `M` rewrites the month digit)
 * h:mm nn -> 2:30 pm
 * mmm is milliseconds
 * ss is seconds
 * M is numeric month (e.g. 2)
 * MM is padded numeric month (e.g. 02)
 * MMM is short month (e.g. Feb)
 * MMMM is long month (e.g. February)
 * yy is 2 digit year
 * yyyy is 4 digit year
 * d is day
 * dd is padded day
 */
export function getTimeString(time: Date, formatString: string, utc = false): string {
    let milHours = time.getHours();
    let minutes = time.getMinutes();
    let date = time.getDate();
    let month = time.getMonth();
    let year = time.getFullYear();
    if (utc) {
        milHours = time.getUTCHours();
        minutes = time.getUTCMinutes();
        date = time.getUTCDate();
        month = time.getUTCMonth();
        year = time.getUTCFullYear();
    }
    let ampm = "am";
    let AMPM = "AM";
    let standHours = milHours;
    if (milHours >= 12) {
        ampm = "pm";
        AMPM = "PM";
    }
    if (milHours > 12) {
        standHours = milHours - 12;
    } else if (milHours === 0) {
        standHours = 12;
    }

    let formatted = formatString;
    formatted = formatted.replace(/hh/g, standHours.toString().padStart(2, "0"));
    formatted = formatted.replace(/HH/g, milHours.toString().padStart(2, "0"));
    formatted = formatted.replace(/H/g, milHours.toString());
    formatted = formatted.replace(/h/g, standHours.toString());

    formatted = formatted.replace(/mmm/g, time.getMilliseconds().toString().padStart(3, "0"));
    formatted = formatted.replace(/mm/g, minutes.toString().padStart(2, "0"));
    formatted = formatted.replace(/ss/g, time.getSeconds().toString().padStart(2, "0"));
    formatted = formatted.replace(/nn/g, ampm);
    formatted = formatted.replace(/NN/g, AMPM);

    formatted = formatted.replace(/dd/g, date.toString().padStart(2, "0"));
    formatted = formatted.replace(/d/g, date.toString());

    // Move these to lower case so they don't conflict with anything else
    formatted = formatted.replace(/MMMM/g, "mmmm");
    formatted = formatted.replace(/MMM/g, "mmm");
    formatted = formatted.replace(/MM/g, (month + 1).toString().padStart(2, "0"));
    formatted = formatted.replace(/M/g, (month + 1).toString());

    formatted = formatted.replace(/mmmm/g, LongMonthLookup[month]);
    formatted = formatted.replace(/mmm/g, ShortMonthLookup[month]);

    formatted = formatted.replace(/yyyy/g, year.toString());
    formatted = formatted.replace(/yy/g, year.toString().substring(2, 4));
    return formatted;
}

export function AddEndingToDate(day: number): string {
    const lastDigit = day % 10;
    if (day > 10 && day < 14) {
        return day.toString() + "th";
    }
    if (lastDigit >= 4) {
        return day.toString() + "th";
    }
    switch (lastDigit) {
        case 1:
        case -1:
            return day.toString() + "st";
        case 2:
        case -2:
            return day.toString() + "nd";
        case 3:
        case -3:
            return day.toString() + "rd";
    }
    return day.toString() + "th";
}

function hoursToAMPM(h: number): number {
    if (h % 12 === 0) {
        return 12;
    }
    return h % 12;
}

/**
 * Generates a human-readable approximate time or time span (e.g. "in 2 minutes", "5 min ago").
 * Ported from legacy gardenspadejs `GenerateApproximateTime`.
 */
export function generateApproximateTime(
    dateInput: Date,
    measuredFrom?: Date,
    useTimespanPrefixes = false,
    shortNotation = false,
    maxAccuracy: "seconds" | "days" = "seconds",
): string {
    const refTime = measuredFrom ?? new Date(Date.now());
    let refTimeIsToday = false;
    {
        const actualTime = new Date(Date.now());
        refTimeIsToday =
            actualTime.getDate() === refTime.getDate() &&
            Math.abs(actualTime.valueOf() - refTime.valueOf()) < 2 * dayMs;
    }
    let timeUntil = dateInput.valueOf() - refTime.valueOf();
    if (useTimespanPrefixes && timeUntil < 0) {
        timeUntil = timeUntil * -1;
    }
    const minutesLabel = shortNotation ? "min" : "minutes";
    const secondsLabel = shortNotation ? "s" : "seconds";
    const hoursLabel = shortNotation ? "hrs" : "hours";
    const inPrefix = useTimespanPrefixes ? "for " : "in ";
    const untilPrefix = useTimespanPrefixes ? "until " : "";
    const paddedMinutes = dateInput.getMinutes().toString().padStart(2, "0");
    if (timeUntil > 0) {
        if (timeUntil < 10 * secondMs) {
            return useTimespanPrefixes ? "instantly" : "now";
        }
        if (timeUntil < minuteMs) {
            return inPrefix + Math.floor(timeUntil / secondMs) + " " + secondsLabel;
        }
        if (timeUntil < hourMs) {
            return inPrefix + Math.floor(timeUntil / minuteMs) + " " + minutesLabel;
        }
        if (timeUntil < 3 * hourMs) {
            const hours = Math.floor(timeUntil / hourMs);
            const minutes = Math.floor((timeUntil - hours * hourMs) / minuteMs);
            if (minutes > 5) {
                return (
                    inPrefix + hours + " " + hoursLabel + (shortNotation ? " " : " and ") + minutes + " " + minutesLabel
                );
            }
            return inPrefix + hours + " " + hoursLabel;
        }
        if (timeUntil < dayMs && refTime.getDay() === dateInput.getDay()) {
            if (refTimeIsToday) {
                if (maxAccuracy === "days") {
                    return untilPrefix + "today";
                }
                return (
                    untilPrefix +
                    "today at " +
                    hoursToAMPM(dateInput.getHours()) +
                    ":" +
                    paddedMinutes +
                    (dateInput.getHours() >= 12 ? "pm" : "am")
                );
            }
            return (
                untilPrefix +
                hoursToAMPM(dateInput.getHours()) +
                ":" +
                paddedMinutes +
                (dateInput.getHours() >= 12 ? "pm" : "am")
            );
        }
        if (timeUntil < 2 * dayMs && (refTime.getDay() + 1) % 7 === dateInput.getDay()) {
            if (refTimeIsToday) {
                if (maxAccuracy === "days") {
                    return untilPrefix + "tomorrow";
                }
                return (
                    untilPrefix +
                    "tomorrow at " +
                    hoursToAMPM(dateInput.getHours()) +
                    ":" +
                    paddedMinutes +
                    (dateInput.getHours() >= 12 ? "pm" : "am")
                );
            }
            if (useTimespanPrefixes) {
                if (maxAccuracy === "days") {
                    return untilPrefix + "the next day";
                }
                return (
                    untilPrefix +
                    hoursToAMPM(dateInput.getHours()) +
                    ":" +
                    paddedMinutes +
                    (dateInput.getHours() >= 12 ? "pm" : "am") +
                    " the next day"
                );
            }
        }
        if (timeUntil < 6 * dayMs) {
            if (maxAccuracy === "days") {
                return untilPrefix + DayLookup[dateInput.getDay()];
            }
            return (
                untilPrefix +
                DayLookup[dateInput.getDay()] +
                " at " +
                hoursToAMPM(dateInput.getHours()) +
                ":" +
                paddedMinutes +
                (dateInput.getHours() >= 12 ? "pm" : "am")
            );
        }
        if (timeUntil < 100 * dayMs) {
            if (maxAccuracy === "days") {
                return untilPrefix + ShortMonthLookup[dateInput.getMonth()] + " " + dateInput.getDate();
            }
            return (
                untilPrefix +
                ShortMonthLookup[dateInput.getMonth()] +
                " " +
                dateInput.getDate() +
                " at " +
                hoursToAMPM(dateInput.getHours()) +
                ":" +
                paddedMinutes +
                (dateInput.getHours() >= 12 ? "pm" : "am")
            );
        }
        return (
            untilPrefix +
            ShortMonthLookup[dateInput.getMonth()] +
            " " +
            dateInput.getDate() +
            " " +
            dateInput.getFullYear()
        );
    }

    const timeAgo = -1 * timeUntil;
    const ago = " ago";
    if (timeAgo < 10 * secondMs) {
        return useTimespanPrefixes ? "instantly" : "now";
    }
    if (timeAgo < minuteMs) {
        return Math.floor(timeAgo / secondMs) + " " + secondsLabel + ago;
    }
    if (timeAgo < hourMs) {
        return Math.floor(timeAgo / minuteMs) + " " + minutesLabel + ago;
    }
    if (timeAgo < 3 * hourMs) {
        const hours = Math.floor(timeAgo / hourMs);
        const minutes = Math.floor((timeAgo - hours * hourMs) / minuteMs);
        if (minutes > 5) {
            return hours + " " + hoursLabel + (shortNotation ? " " : " and ") + minutes + " " + minutesLabel + ago;
        }
        return hours + " " + hoursLabel + ago;
    }
    if (timeAgo < dayMs && refTime.getDay() === dateInput.getDay()) {
        if (maxAccuracy === "days") {
            return untilPrefix + "today";
        }
        return (
            untilPrefix +
            "today at " +
            hoursToAMPM(dateInput.getHours()) +
            ":" +
            paddedMinutes +
            (dateInput.getHours() >= 12 ? "pm" : "am")
        );
    }
    if (timeAgo < 2 * dayMs && (refTime.getDay() + 1) % 7 === dateInput.getDay()) {
        if (maxAccuracy === "days") {
            return untilPrefix + "yesterday";
        }
        return (
            untilPrefix +
            "yesterday at " +
            hoursToAMPM(dateInput.getHours()) +
            ":" +
            paddedMinutes +
            (dateInput.getHours() >= 12 ? "pm" : "am")
        );
    }
    if (timeAgo < 6 * dayMs) {
        if (maxAccuracy === "days") {
            return untilPrefix + DayLookup[dateInput.getDay()];
        }
        return (
            untilPrefix +
            DayLookup[dateInput.getDay()] +
            " at " +
            hoursToAMPM(dateInput.getHours()) +
            ":" +
            paddedMinutes +
            (dateInput.getHours() >= 12 ? "pm" : "am")
        );
    }
    if (timeAgo < 100 * dayMs) {
        if (maxAccuracy === "days") {
            return untilPrefix + ShortMonthLookup[dateInput.getMonth()] + " " + dateInput.getDate();
        }
        return (
            untilPrefix +
            ShortMonthLookup[dateInput.getMonth()] +
            " " +
            dateInput.getDate() +
            " at " +
            hoursToAMPM(dateInput.getHours()) +
            ":" +
            paddedMinutes +
            (dateInput.getHours() >= 12 ? "pm" : "am")
        );
    }
    return (
        untilPrefix + ShortMonthLookup[dateInput.getMonth()] + " " + dateInput.getDate() + " " + dateInput.getFullYear()
    );
}

/**
 * Converts a date to a UTC date string in the format "day-month-year". Month is zero indexed. This is a legacy function
 * that the indexing of irrigation events relies upon. Fixing this requires a small DB migration.
 *
 * Should never be presented as a human-readable string.
 * @param date - Date object or timestamp
 * @deprecated
 * @returns UTC date string
 */
export function utcDateString(date: number | Date): string {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    return `${date.getUTCDate()}-${date.getUTCMonth()}-${date.getUTCFullYear()}`;
}

/**
 * Rebuilds a Date from its UTC calendar components, truncating to whole seconds.
 * This is a legacy helper relied upon by recurrence-rule (RRule) start times, which
 * expect second-precision UTC instants.
 *
 * @param t - The Date to convert
 * @returns A new Date built from the UTC components of `t`, with milliseconds dropped
 */
export function timeToUTCConversion(t: Date): Date {
    return new Date(
        Date.UTC(
            t.getUTCFullYear(),
            t.getUTCMonth(),
            t.getUTCDate(),
            t.getUTCHours(),
            t.getUTCMinutes(),
            t.getUTCSeconds(),
        ),
    );
}

export enum LAST_CONTACT_FALLBACK_MODE {
    CURRENT_TIME = "CURRENT_TIME",
    ORIGINAL_TIME = "ORIGINAL_TIME",
    ERROR = "ERROR",
}

/**
 * Updates the last contact timestamp with validation and fallback handling.
 *
 * This function ensures that last contact times are monotonically increasing and handles
 * edge cases where timestamps may be affected by clock drift or synchronization issues.
 * It's designed for device communication scenarios where timestamps must be reliable.
 *
 * **Validation Rules:**
 * - Last contact generally cannot decrease (takes max of original and new values)
 * - Detects significant backward time jumps (>1 day)
 * - Logs warnings for minor backward time jumps (>100ms) for monitoring
 * - Always rejects timestamps more than 3 seconds in the future (prevents clock drift)
 *
 * **Backward Time Jump Detection:**
 * - **Major jump (>1 day backward)**: Triggers fallback, assumes server clock issue
 * - **Minor jump (>100ms backward)**: Logs info message, continues with original timestamp
 * - Both scenarios indicate potential clock synchronization problems worth monitoring
 *
 * **Future Timestamp Handling:**
 * The function always rejects timestamps more than 3 seconds in the future. This 3-second
 * buffer accounts for minor clock drift and network latency between server and device, but
 * prevents accepting timestamps from devices with significant clock synchronization issues.
 *
 * **Fallback Behavior:**
 * When validation fails, the function behavior depends on the fallbackMode:
 * - **CURRENT_TIME**: Returns current server time (always returns a Date, safe for live updates)
 * - **ORIGINAL_TIME**: Returns the original timestamp unchanged (may return undefined if original was undefined)
 * - **ERROR**: Throws an error with detailed diagnostics (for strict validation requirements)
 *
 * **Important Edge Cases:**
 *
 * 1. **ORIGINAL_TIME with undefined original:**
 *    When using ORIGINAL_TIME fallback mode and the original timestamp is undefined, validation
 *    failures will return undefined. This preserves type safety and makes it explicit that no
 *    valid historical timestamp exists.
 *
 * 2. **ORIGINAL_TIME with invalid original (self-correction):**
 *    If the original timestamp itself is more than 3 seconds in the future (indicating a previous
 *    error or system clock issue), the function will attempt to self-correct by validating the new timestamp.
 *    This allows the system to recover from corrupted historical timestamps by accepting a valid new timestamp.
 *    If the new timestamp is also invalid, the fallback behavior will cause the function to return undefined.
 *    This self-correction is logged as an error for monitoring and investigation.
 *
 *    Note that this means sometimes this function may return undefined even if both new and original timestamps are
 *    defined
 *
 * @param originalTimestamp - The existing last contact timestamp, or undefined if none exists.
 *                           When undefined and validation fails with ORIGINAL_TIME mode,
 *                           the function returns undefined (not epoch).
 * @param newTimestamp - The proposed new last contact timestamp from the latest communication.
 * @param fallbackMode - Determines behavior when validation fails. Options:
 *                      - CURRENT_TIME: Use current server time (always returns Date)
 *                      - ORIGINAL_TIME: Preserve original value (may return undefined)
 *                      - ERROR: Throw detailed error
 *
 * @returns A validated Date object, or undefined when originalTimestamp is undefined and
 *          ORIGINAL_TIME fallback is used. The return type preserves the nullability of
 *          the originalTimestamp parameter for type safety.
 *
 * @throws {Error} When fallbackMode is ERROR and validation fails. Error message includes:
 *                 - Which validation check failed
 *                 - The problematic timestamp values
 *                 - Current time for comparison
 *                 - Diagnostic information to identify the root cause
 *
 * @example
 * // Basic usage with current time fallback (recommended - always returns Date)
 * const updated = getUpdatedLastContactTimestamp(
 *   device.lastContact,
 *   new Date(),
 *   LAST_CONTACT_FALLBACK_MODE.CURRENT_TIME
 * );
 * // updated is always a Date
 *
 * @example
 * // Preserve original timestamp when new data is suspect
 * const updated = getUpdatedLastContactTimestamp(
 *   device.lastContact,
 *   suspectTimestamp,
 *   LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME
 * );
 * // updated may be undefined if device.lastContact was undefined and suspectTimestamp failed validation
 *
 * @example
 * // Handle the edge case where original is undefined with ORIGINAL_TIME mode
 * const updated = getUpdatedLastContactTimestamp(
 *   device.lastContact, // might be undefined for new device
 *   uplinkTimestamp,
 *   LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME
 * );
 * // Provide fallback if needed
 * const finalTimestamp = updated ?? new Date(); // Use current time if undefined
 *
 * @example
 * // Self-correction example: corrupted original timestamp
 * // If device.lastContact is somehow in the future (e.g., from a previous system error),
 * // the function will attempt to recover by validating the new timestamp independently
 * const updated = getUpdatedLastContactTimestamp(
 *   corruptedFutureTimestamp, // e.g., 5 seconds in the future
 *   validNewTimestamp,         // valid current timestamp
 *   LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME
 * );
 * // Function logs an error and recursively validates validNewTimestamp against epoch
 * // Result: Returns validNewTimestamp if it's valid, allowing recovery from corruption
 *
 * @example
 * // Strict validation that throws on invalid timestamps
 * try {
 *   const updated = getUpdatedLastContactTimestamp(
 *     device.lastContact,
 *     uplinkTimestamp,
 *     LAST_CONTACT_FALLBACK_MODE.ERROR
 *   );
 * } catch (err) {
 *   logger.error('Invalid timestamp received from device', err);
 *   // Handle the error appropriately
 * }
 */
export function getUpdatedLastContactTimestamp(
    originalTimestamp: Date | undefined,
    newTimestamp: Date | undefined,
    fallbackMode: LAST_CONTACT_FALLBACK_MODE,
): Date | undefined {
    if (!originalTimestamp && !newTimestamp) {
        return undefined;
    }

    let useFallback: boolean = false;
    let errorMessage: string | undefined;

    const originalValue = originalTimestamp?.valueOf() ?? 0;
    const newValue = newTimestamp?.valueOf() ?? 0;
    const lastContactValue = Math.max(originalValue, newValue);
    const currentTimeValue = TimeOverride.valueOf();

    // only detect the last contact being wound back if both the original timestamp and the new timestamp are defined
    // if they are not defined, we only need to check we aren't advancing this into the future
    if (originalTimestamp && newTimestamp) {
        // Detect major backward time jump (>1 day) - likely indicates server clock issue
        if (originalValue > newValue + dayMs) {
            const backwardJump = originalValue - newValue;
            errorMessage =
                `Last contact timestamp validation failed: new timestamp is (${(backwardJump / dayMs).toFixed(1)} days) behind original. ` +
                `Original: ${new Date(originalValue).toISOString()}, ` +
                `New: ${new Date(newValue).toISOString()}. ` +
                `This likely indicates a server clock issue or system time adjustment.`;

            if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ERROR) {
                throw new Error(errorMessage);
            }
            console.warn(errorMessage);
            useFallback = true;
        }
        // Detect minor backward time jump (>100ms) - worth logging for monitoring
        else if (originalValue > newValue + 100) {
            const backwardJump = originalValue - newValue;
            console.info(
                `Last contact timestamp moving backward by ${backwardJump}ms. ` +
                    `Original: ${new Date(originalValue).toISOString()}, ` +
                    `New: ${new Date(newValue).toISOString()}. ` +
                    `This may indicate minor clock drift or out-of-order message processing.`,
            );
        }
    }

    // Always reject timestamps more than 3 seconds into the future to prevent clock drift issues
    if (lastContactValue > currentTimeValue + secondMs * 3) {
        const futureOffset = lastContactValue - currentTimeValue;
        errorMessage =
            `Last contact timestamp validation failed: timestamp is ${futureOffset}ms in the future (${(futureOffset / 1000).toFixed(1)}s). ` +
            `Last contact: ${new Date(lastContactValue).toISOString()}, ` +
            `Current time: ${new Date(currentTimeValue).toISOString()}, ` +
            `Maximum allowed future offset: 3000ms (3s). ` +
            `This likely indicates clock synchronization issues on the device.`;

        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ERROR) {
            throw new Error(errorMessage);
        }
        console.warn(errorMessage);
        useFallback = true;
    }

    if (useFallback) {
        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.CURRENT_TIME) {
            return new Date(currentTimeValue);
        }
        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME) {
            // Self-correction: If the original timestamp itself is invalid (>3s in future),
            // attempt to recover by validating the new timestamp against epoch instead.
            // This allows the system to recover from corrupted historical timestamps.
            if (originalValue > currentTimeValue + secondMs * 3) {
                console.error(
                    `Last contact timestamp self-correction triggered: ` +
                        `Original timestamp is ${originalValue - currentTimeValue}ms in the future (${((originalValue - currentTimeValue) / 1000).toFixed(1)}s). ` +
                        `Current time: ${new Date(currentTimeValue).toISOString()}. ` +
                        `Original lastContact timestamp: ${new Date(originalValue).toISOString()}, ` +
                        `new lastContact timestamp: ${new Date(newValue).toISOString()}. ` +
                        `Attempting to recover by validating new timestamp against epoch. ` +
                        `This indicates a previous system error or clock synchronization issue that requires investigation.`,
                );

                // Recursive call with undefined as the original - this validates newTimestamp on its own merits
                // If newTimestamp is also invalid, it will trigger fallback again (returning undefined for ORIGINAL_TIME)
                // If newTimestamp is valid, we successfully recover with a good timestamp
                return getUpdatedLastContactTimestamp(undefined, newTimestamp, fallbackMode);
            }

            // Return original timestamp as-is (may be undefined)
            // This preserves type safety and makes it explicit when no valid historical timestamp exists
            return originalTimestamp;
        }
        // this should not happen, error fallbacks should cause an early function exit. This is in case the code changes
        // in the future.
        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ERROR) {
            throw new Error(
                "Unknown error validating last contact update. Should have thrown and had an early exit, " +
                    "but instead somehow made it this far.",
            );
        }
    }

    return new Date(lastContactValue);
}

/**
 * Formats a timestamp to a human readable string in the locale specific format
 * Example for en-CA, would return "Nov 26, 2025, 04:57 p.m."
 * @param epochTimeMs - The timestamp in milliseconds
 * @returns The formatted time string
 */
export function formatTime(epochTimeMs: number): string {
    return new Date(epochTimeMs).toLocaleString("en-CA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}
